import { logger } from '@/lib/clientSideLogger';
import { timezones } from './locations';

export const timeSlot = ['MORNING', 'AFTERNOON', 'EVENING'] as const; //deprecated
export const dayOfWeek = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export const AdminTimezone = 'America/Toronto';
export const UserDefaultTimezone = 'America/Toronto';

export function toArbitraryUtc(
  day: DayOfWeek,
  hour: number,
  minute: number,
  timezone: string
): string {
  return toArbitraryDate(day, hour, minute, timezone).toISOString();
}

export function toArbitraryDate(
  day: DayOfWeek,
  hour: number,
  minute: number,
  timezone: string
): Date {
  let timezoneOffsetString: string;

  if (timezone === 'UTC') {
    timezoneOffsetString = 'Z';
  } else {
    const foundTimezone = timezones.find((t) => t.value === timezone);
    if (foundTimezone && typeof foundTimezone.offset === 'string') {
      timezoneOffsetString = foundTimezone.offset;
    } else {
      logger(
        'log',
        `[toArbitraryDate] Timezone "${timezone}" not found in configured timezones or missing offset. Falling back to UTC.`
      );
      timezoneOffsetString = 'Z'; // Fallback to UTC
    }
  }

  const dateString = `${dayOfWeekAsArbitraryDate[day]}T${hour
    .toString()
    .padStart(
      2,
      '0'
    )}:${minute.toString().padStart(2, '0')}:00${timezoneOffsetString}`;

  return new Date(dateString);
}

const dayOfWeekAsArbitraryDate = {
  Sunday: '2025-03-16',
  Monday: '2025-03-17',
  Tuesday: '2025-03-18',
  Wednesday: '2025-03-19',
  Thursday: '2025-03-20',
  Friday: '2025-03-21',
  Saturday: '2025-03-22',
} as const;

export type DeprecatedTimeSlot = (typeof timeSlot)[number];
export type DayOfWeek = (typeof dayOfWeek)[number];
export function getDayNumber(day: DayOfWeek): number {
  const index = dayOfWeek.indexOf(day);
  // Convert from Monday-based to Sunday-based numbering (as in Date.getDay())
  return (index + 1) % 7;
}

export function getDayOfWeek(date: Date): DayOfWeek {
  const dayIndex = date.getDay();
  // Add 7 before modulo to handle negative numbers correctly
  return dayOfWeek[(dayIndex - 1 + 7) % 7];
}

export class TimeSlot {
  from: Date;
  to: Date;

  constructor(from: Date, to: Date) {
    if (from > to) {
      throw new Error('From date must be before to date');
    }
    this.from = from;
    this.to = to;
  }

  static createTimeSlot(
    fromHour: number,
    fromMinute: number,
    toHour: number,
    toMinute: number,
    day: DayOfWeek,
    timezone: string
  ) {
    const from = toArbitraryDate(day, fromHour, fromMinute, timezone);
    let to = toArbitraryDate(day, toHour, toMinute, timezone);
    if (from > to) {
      // we need to adjust the to date to the next day
      const DAY_IN_MS = 24 * 60 * 60 * 1000;
      to = new Date(to.getTime() + DAY_IN_MS);
    }
    return new TimeSlot(from, to);
  }

  lengthInHours(): number {
    const msInHour = 60 * 60 * 1000;
    return (this.to.getTime() - this.from.getTime()) / msInHour;
  }

  getTimeSlotHours(timezone: string) {
    const fromDate = this.from.toLocaleString('en-US', {
      timeZone: timezone,
      hour12: false,
    });
    const toDate = this.to.toLocaleString('en-US', {
      timeZone: timezone,
      hour12: false,
    });

    const [fromHour, fromMinute] = fromDate
      .split(', ')[1]
      .split(':')
      .map(Number);
    const [toHour, toMinute] = toDate.split(', ')[1].split(':').map(Number);

    return {
      fromHour,
      fromMinute,
      toHour,
      toMinute,
    };
  }

  isTimeSlotsOverlapping(slot: TimeSlot): boolean {
    return this.from < slot.to && this.to > slot.from;
  }
}

export function groupSlotsByDay(
  schedule: TimeSlot[],
  timezone: string
): Record<DayOfWeek, TimeSlot[]> {
  return dayOfWeek.reduce(
    (acc, day) => {
      acc[day] = schedule.filter((slot) => {
        // Convert the slot's time to the specified timezone
        const fromDateInTimezone = new Date(
          slot.from.toLocaleString('en-US', {
            timeZone: timezone,
          })
        );
        return getDayOfWeek(fromDateInTimezone) === day;
      });
      return acc;
    },
    {} as Record<DayOfWeek, TimeSlot[]>
  );
}
